import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const getCurrentUser = () => localStorage.getItem('notes_user') || '';

const fetchWithAuth = async (url, options = {}) => {
  const token = getToken();
  const res = await fetch(url, {
    ...options,
    headers: { ...options.headers, 'x-token': token }
  });
  if (res.status === 401) {
    localStorage.removeItem('notes_token');
    localStorage.removeItem('notes_user');
    window.location.href = '/';
  }
  return res;
};

export default function ProjectDocDetail() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const currentUser = getCurrentUser();
  const fileInputRef = useRef(null);

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadProject();
  }, [projectId]);

  const loadProject = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/doc-projects`);
      const data = await res.json();
      const p = data.find(proj => proj.id === projectId);
      if (p) {
        setProject(p);
        // 默认选中第一个文件夹
        if (p.folders && p.folders.length > 0 && !selectedFolder) {
          setSelectedFolder(p.folders[0].id);
        }
      }
    } catch (e) {
      console.error('加载项目失败:', e);
    }
    setLoading(false);
  };

  const hasPermission = () => {
    if (!project) return false;
    return project.manager === currentUser || 
           project.members.includes(currentUser) || 
           currentUser === 'jch';
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return alert('请输入文件夹名称');
    
    try {
      const res = await fetchWithAuth(`${API_URL}/api/doc-projects/${projectId}/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFolderName })
      });
      
      if (res.ok) {
        const folder = await res.json();
        setProject(prev => ({
          ...prev,
          folders: [...(prev.folders || []), folder]
        }));
        setSelectedFolder(folder.id);
        setShowNewFolder(false);
        setNewFolderName('');
      }
    } catch (e) {
      alert('创建失败');
    }
  };

  const handleDeleteFolder = async (folderId, folderName) => {
    if (!confirm(`确定删除文件夹"${folderName}"？所有文件都会被删除！`)) return;
    
    try {
      await fetchWithAuth(`${API_URL}/api/doc-folders/${folderId}`, { method: 'DELETE' });
      setProject(prev => ({
        ...prev,
        folders: prev.folders.filter(f => f.id !== folderId)
      }));
      if (selectedFolder === folderId) {
        setSelectedFolder(null);
      }
    } catch (e) {
      alert('删除失败');
    }
  };

  const handleUpload = async (files) => {
    if (!selectedFolder) return alert('请先选择文件夹');
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    
    try {
      const res = await fetchWithAuth(`${API_URL}/api/doc-folders/${selectedFolder}/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        // 更新本地状态
        setProject(prev => {
          const newFolders = prev.folders.map(f => {
            if (f.id === selectedFolder) {
              return { ...f, files: [...(f.files || []), ...data.files] };
            }
            return f;
          });
          return { ...prev, folders: newFolders };
        });
      } else {
        alert('上传失败');
      }
    } catch (e) {
      alert('上传失败');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteFile = async (fileId, fileName) => {
    if (!confirm(`确定删除文件"${fileName}"？`)) return;
    
    try {
      await fetchWithAuth(`${API_URL}/api/doc-files/${fileId}`, { method: 'DELETE' });
      setProject(prev => {
        const newFolders = prev.folders.map(f => {
          if (f.id === selectedFolder) {
            return { ...f, files: f.files.filter(file => file.id !== fileId) };
          }
          return f;
        });
        return { ...prev, folders: newFolders };
      });
    } catch (e) {
      alert('删除失败');
    }
  };

  const handleDownload = async (fileId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/doc-files/${fileId}/download`);
      const data = await res.json();
      if (data.success && data.url) {
        window.open(data.url, '_blank');
      }
    } catch (e) {
      alert('获取下载链接失败');
    }
  };

  const handlePreview = async (fileId, fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
    const pdfExts = ['pdf'];
    const officeExts = ['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'];
    
    try {
      const res = await fetchWithAuth(`${API_URL}/api/doc-files/${fileId}/preview`);
      const data = await res.json();
      if (data.success && data.url) {
        // Office文档使用微软在线预览
        if (officeExts.includes(ext)) {
          const previewUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(data.url)}`;
          window.open(previewUrl, '_blank');
        } else if (imageExts.includes(ext) || pdfExts.includes(ext)) {
          // 图片和PDF直接打开
          window.open(data.url, '_blank');
        } else {
          alert('该文件类型暂不支持在线预览，请下载后查看');
        }
      }
    } catch (e) {
      alert('获取预览链接失败');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(1)} ${units[i]}`;
  };

  if (loading) return <div className="loading">加载中...</div>;
  if (!project) return <div style={{ padding: 40, textAlign: 'center' }}>项目不存在或无权限访问</div>;

  const currentFolder = project.folders?.find(f => f.id === selectedFolder);

  return (
    <div className="dashboard-container">
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button 
            onClick={() => navigate('/work-logs/doc-projects')}
            style={{
              padding: '6px 12px', background: '#fff', border: '1px solid #d9d9d9',
              borderRadius: 6, cursor: 'pointer', fontSize: 14
            }}
          >
            ← 返回
          </button>
          <h2 style={{ margin: 0 }}>📂 {project.name}</h2>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, marginTop: 24 }}>
        {/* 左侧文件夹列表 */}
        <div style={{
          width: 280, flexShrink: 0, background: '#fff',
          borderRadius: 12, border: '1px solid #e8e8e8', padding: 16
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ margin: 0, fontSize: 15 }}>📂 文件夹</h4>
            {hasPermission() && (
              <button 
                onClick={() => setShowNewFolder(true)}
                style={{
                  padding: '4px 8px', background: '#1890ff', color: '#fff',
                  border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12
                }}
              >
                + 新建
              </button>
            )}
          </div>

          {showNewFolder && (
            <div style={{ marginBottom: 12, padding: 8, background: '#f5f5f5', borderRadius: 6 }}>
              <input
                type="text"
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                placeholder="文件夹名称"
                autoFocus
                style={{
                  width: '100%', padding: '6px 8px', border: '1px solid #d9d9d9',
                  borderRadius: 4, fontSize: 13, boxSizing: 'border-box'
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCreateFolder();
                  if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); }
                }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button onClick={handleCreateFolder} style={{ padding: '4px 8px', fontSize: 12, background: '#1890ff', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>确定</button>
                <button onClick={() => { setShowNewFolder(false); setNewFolderName(''); }} style={{ padding: '4px 8px', fontSize: 12, background: '#fff', border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer' }}>取消</button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {project.folders?.map(folder => (
              <div
                key={folder.id}
                onClick={() => setSelectedFolder(folder.id)}
                style={{
                  padding: '10px 12px', borderRadius: 6, cursor: 'pointer',
                  background: selectedFolder === folder.id ? '#e6f7ff' : 'transparent',
                  border: selectedFolder === folder.id ? '1px solid #91d5ff' : '1px solid transparent',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}
              >
                <span style={{ fontSize: 14 }}>📁 {folder.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, color: '#999' }}>{folder.files?.length || 0}</span>
                  {hasPermission() && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder.id, folder.name);
                      }}
                      style={{
                        padding: '2px 4px', background: 'none', border: 'none',
                        cursor: 'pointer', fontSize: 12, color: '#f5222d'
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))}
            {(!project.folders || project.folders.length === 0) && (
              <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>
                暂无文件夹
              </div>
            )}
          </div>
        </div>

        {/* 右侧文件列表 */}
        <div style={{ flex: 1, background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', padding: 16 }}>
          {currentFolder ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h4 style={{ margin: 0, fontSize: 15 }}>📄 {currentFolder.name} - 文件列表</h4>
                {hasPermission() && (
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      multiple
                      onChange={e => handleUpload(e.target.files)}
                      style={{ display: 'none' }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      style={{
                        padding: '6px 12px', background: '#1890ff', color: '#fff',
                        border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13
                      }}
                    >
                      {uploading ? '上传中...' : '📤 上传文件'}
                    </button>
                  </div>
                )}
              </div>

              {currentFolder.files && currentFolder.files.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {currentFolder.files.map(file => (
                    <div
                      key={file.id}
                      style={{
                        padding: '12px 16px', background: '#fafafa',
                        borderRadius: 8, border: '1px solid #f0f0f0',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                          📄 {file.name}
                        </div>
                        <div style={{ fontSize: 12, color: '#999', display: 'flex', gap: 12 }}>
                          <span>{formatSize(file.size)}</span>
                          <span>上传人：{file.uploadedBy}</span>
                          <span>{new Date(file.uploadedAt).toLocaleString('zh-CN')}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => handlePreview(file.id, file.name)}
                          style={{
                            padding: '4px 10px', background: '#e6f7ff', border: '1px solid #91d5ff',
                            borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#1890ff'
                          }}
                        >
                          预览
                        </button>
                        <button
                          onClick={() => handleDownload(file.id)}
                          style={{
                            padding: '4px 10px', background: '#f6ffed', border: '1px solid #b7eb8f',
                            borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#52c41a'
                          }}
                        >
                          下载
                        </button>
                        {hasPermission() && (
                          <button
                            onClick={() => handleDeleteFile(file.id, file.name)}
                            style={{
                              padding: '4px 10px', background: '#fff1f0', border: '1px solid #ffa39e',
                              borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#f5222d'
                            }}
                          >
                            删除
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>📄</div>
                  <p>暂无文件</p>
                  {hasPermission() && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        padding: '8px 16px', background: '#1890ff', color: '#fff',
                        border: 'none', borderRadius: 6, cursor: 'pointer', marginTop: 8
                      }}
                    >
                      上传第一个文件
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: 60, textAlign: 'center', color: '#999' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📁</div>
              <p>请选择左侧文件夹查看文件</p>
              {hasPermission() && project.folders?.length === 0 && (
                <button
                  onClick={() => setShowNewFolder(true)}
                  style={{
                    padding: '8px 16px', background: '#1890ff', color: '#fff',
                    border: 'none', borderRadius: 6, cursor: 'pointer', marginTop: 8
                  }}
                >
                  创建第一个文件夹
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
